import React, { useState } from 'react';
import styles from '../pages/UserDetailsPage.module.css';
import api, { uploadAdminPaymentProof } from '../utils/api';

const transactionStatusOptions = ['Pending', 'Completed', 'Failed'];
const marginAdjustmentStatusOptions = ['Completed', 'Cancelled'];

const Transactions = ({ userId, transactions, setTransactions, onImageClick, onSaveSuccess }) => {
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [editedTransactionData, setEditedTransactionData] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleEditTransaction = (transaction) => {
        setEditingTransaction(transaction._id);
        setEditedTransactionData({ txnStatus: transaction.txnStatus });
        setSelectedFile(null);
    };

    const handleEditedTransactionChange = (e) => {
        const { name, value } = e.target;
        setEditedTransactionData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleSaveTransaction = async (transactionId, txnType) => {
        // Validation: Withdrawal needs proof if being completed
        if (txnType === 'withdraw' && editedTransactionData.txnStatus === 'Completed' && !selectedFile) {
            const txn = transactions.find(t => t._id === transactionId);
            if (!txn.paymentProof) {
                alert('Please upload payment proof for completing withdrawal');
                return;
            }
        }

        try {
            setIsUploading(true);
            
            // 1. Upload proof if selected
            if (selectedFile) {
                const formData = new FormData();
                formData.append('paymentProof', selectedFile);
                await uploadAdminPaymentProof(userId, transactionId, formData);
            }

            // 2. Update status
            const res = await api.put(`/api/admin/transactions/${transactionId}`, editedTransactionData);
            
            setTransactions(transactions.map((txn) => (txn._id === transactionId ? res.data : txn)));
            setEditingTransaction(null);
            setEditedTransactionData({});
            setSelectedFile(null);
            alert('Transaction updated successfully!');
            if (onSaveSuccess) {
                onSaveSuccess();
            }
        } catch (err) {
            console.error('[Admin Frontend] Failed to update transaction', err);
            alert('Failed to update transaction');
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancelEditTransaction = () => {
        setEditingTransaction(null);
        setEditedTransactionData({});
        setSelectedFile(null);
    };

    return (
        <div className={styles['transactions-list']}>
            {transactions.length === 0 ? (
                <p>No transactions found for this user.</p>
            ) : (
                transactions.map(txn => {
                    const isMarginOrAdjustment = txn.txnType === 'Margin' || txn.txnType === 'Adjustments';
                    const currentOptions = isMarginOrAdjustment ? marginAdjustmentStatusOptions : transactionStatusOptions;

                    return (
                        <div key={txn._id} className={styles['transaction-card']}>
                            {editingTransaction === txn._id ? (
                                <div className={styles['edit-transaction-form']}>
                                    <div className={styles['form-group']}>
                                        <label>Type:</label>
                                        <p>{txn.txnType}</p>
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Amount:</label>
                                        <p>{txn.txnAmount}</p>
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Status:</label>
                                        <select
                                            name="txnStatus"
                                            value={editedTransactionData.txnStatus || ''}
                                            onChange={handleEditedTransactionChange}
                                            disabled={txn.txnStatus === 'Cancelled'}
                                        >
                                            {currentOptions.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                            {/* Ensure current status is always visible if not in list */}
                                            {!currentOptions.includes(txn.txnStatus) && (
                                                <option value={txn.txnStatus}>{txn.txnStatus}</option>
                                            )}
                                        </select>
                                    </div>
                                    {txn.txnType === 'withdraw' && (
                                        <div className={styles['form-group']} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <label>Upload Payment Proof:</label>
                                            <input type="file" onChange={handleFileChange} accept="image/*,application/pdf" />
                                        </div>
                                    )}
                                    <div className={styles['transaction-card-actions']}>
                                        <button 
                                            className={styles['btn-approve']} 
                                            onClick={() => handleSaveTransaction(txn._id, txn.txnType)}
                                            disabled={isUploading}
                                        >
                                            {isUploading ? 'Saving...' : 'Save'}
                                        </button>
                                        <button className={styles['delete-button']} onClick={() => handleCancelEditTransaction()}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <h3>Transaction ID: {txn._id}</h3>
                                    <div className={styles['info-group']}>
                                        <span className={styles['info-label']}>Type:</span>
                                        <span>{txn.txnType}</span>
                                    </div>
                                    <div className={styles['info-group']}>
                                        <span className={styles['info-label']}>Amount:</span>
                                        <span style={{ color: txn.txnAmount < 0 ? 'red' : 'inherit' }}>
                                            {txn.txnAmount}
                                        </span>
                                    </div>
                                    <div className={styles['info-group']}>
                                        <span className={styles['info-label']}>Status:</span>
                                        <span style={{ 
                                            color: txn.txnStatus === 'Completed' ? 'green' : (txn.txnStatus === 'Failed' || txn.txnStatus === 'Cancelled' ? 'red' : 'orange'),
                                            fontWeight: 'bold'
                                        }}>
                                            {txn.txnStatus}
                                        </span>
                                    </div>
                                    <div className={styles['info-group']}>
                                        <span className={styles['info-label']}>Time:</span>
                                        <span>{new Date(txn.txnTime).toLocaleString()}</span>
                                    </div>
                                    {txn.paymentProof && (
                                        <div className={styles['info-group']}>
                                            <span className={styles['info-label']}>Payment Proof:</span>
                                            {txn.paymentProof.toLowerCase().endsWith('.pdf') ? (
                                                <a href={txn.paymentProof} target="_blank" rel="noopener noreferrer">View PDF</a>
                                            ) : (
                                                <img
                                                    src={txn.paymentProof}
                                                    alt="Payment Proof"
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }}
                                                    onClick={() => onImageClick(txn.paymentProof)}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className={styles['transaction-card-actions']}>
                                        {txn.txnStatus !== 'Cancelled' && (
                                            <button className={styles['btn-approve']} onClick={() => handleEditTransaction(txn)}>Edit Status</button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};

export default Transactions;
